import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Copy, Loader2, Plus } from "lucide-react";
import { PageLoader } from "../context/AuthContext";
import { ImportCandidateCard } from "../components/ImportCandidateCard";
import { ScreenshotStrip, ScreenshotViewer } from "../components/ImportShots";
import { api, formatApiError } from "../lib/api";
import { keyOf, labelOf, newRequestId, splitName } from "../lib/doorGuests";
import { duplicateHints, manualRow, orderRows, toRow } from "../lib/importReview";

const MAX_FILES = 15;
const MAX_BYTES = 10 * 1024 * 1024;
const CONCURRENCY = 3;

const ResultView = ({ id, result }) => (
  <div data-testid="import-result" className="rounded-3xl border border-slate-200 bg-white p-6">
    <p className="flex items-center gap-2 text-lg font-bold text-emerald-700"><CheckCircle2 className="h-6 w-6" /> <span data-testid="import-result-count">Imported {result.saved_count} guest{result.saved_count === 1 ? "" : "s"}</span></p>
    {result.failed.length > 0 && (
      <ul data-testid="import-result-failed" className="mt-3 space-y-1 text-sm text-red-700">
        {result.failed.map((f) => <li key={f.index}>Row {f.index + 1}: {f.reason}</li>)}
      </ul>
    )}
    <div className="mt-6 flex flex-col gap-2 sm:flex-row">
      <Link to={`/events/${id}`} data-testid="import-result-guest-list" className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl border border-slate-200 font-semibold text-slate-700">Back to guest list</Link>
      <Link to={`/events/${id}/door`} data-testid="import-result-door" className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-blue-700 font-semibold text-white">Open door check-in</Link>
    </div>
  </div>
);

export default function DoorImport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const [shots, setShots] = useState([]);
  const [rows, setRows] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const importId = useRef(newRequestId());
  const shotsRef = useRef(shots);
  shotsRef.current = shots;

  useEffect(() => {
    api.get(`/events/${id}/door-guests`).then((r) => setExisting(r.data)).catch((e) => setError(formatApiError(e)));
    return () => shotsRef.current.forEach((s) => URL.revokeObjectURL(s.url));
  }, [id]);

  const patchShot = (key, patch) => setShots((list) => list.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const indexOfKey = (key) => shotsRef.current.findIndex((s) => s.key === key);

  const extract = async (shot) => {
    patchShot(shot.key, { status: "extracting", error: "" });
    const form = new FormData();
    form.append("files", shot.file);
    try {
      const { data } = await api.post(`/events/${id}/door-guests/import/extract`, form);
      const img = data.images[0];
      if (!img.ok) return patchShot(shot.key, { status: "error", error: img.error });
      setRows((list) => [...list.filter((r) => r.shot_key !== shot.key),
        ...data.candidates.map((c) => ({ ...toRow(c, null), shot_key: shot.key }))]);
      patchShot(shot.key, { status: "done", rows: data.candidates.length });
    } catch (err) {
      patchShot(shot.key, { status: "error", error: err.response ? formatApiError(err) : "Network error — check your connection and retry." });
    }
  };

  const runQueue = async (queue) => {
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length) await extract(queue.shift());
    });
    await Promise.all(workers);
  };

  const pick = (files) => {
    setError("");
    const room = MAX_FILES - shots.length;
    if (files.length > room) setError(`Up to ${MAX_FILES} screenshots per import; only the first ${Math.max(room, 0)} were added.`);
    const added = files.slice(0, Math.max(room, 0)).map((file) => ({
      key: newRequestId(), file, url: URL.createObjectURL(file),
      status: file.size > MAX_BYTES ? "error" : "waiting", error: file.size > MAX_BYTES ? "This image is larger than 10MB" : "", rows: 0,
    }));
    setShots((list) => [...list, ...added]);
    runQueue(added.filter((s) => s.status === "waiting"));
  };

  const removeShot = (i) => {
    const shot = shots[i];
    URL.revokeObjectURL(shot.url);
    setShots((list) => list.filter((s) => s.key !== shot.key));
    setRows((list) => list.filter((r) => r.shot_key !== shot.key));
  };

  const withShotIndex = useMemo(() => rows.map((r) => ({ ...r, screenshot_index: r.shot_key ? indexOfKey(r.shot_key) : null })), [rows, shots]); // eslint-disable-line react-hooks/exhaustive-deps
  const ordered = useMemo(() => orderRows(withShotIndex), [withShotIndex]);
  const existingKeys = useMemo(() => new Set((existing?.guests || []).map((g) => keyOf(g.identifier))), [existing]);
  const hints = useMemo(() => duplicateHints(withShotIndex, existingKeys), [withShotIndex, existingKeys]);
  const selected = ordered.filter((r) => r.include);
  const busy = shots.some((s) => s.status === "extracting" || s.status === "waiting");
  const label = labelOf(existing?.guest_identifier_label);

  const updateRow = (row) => setRows((list) => list.map((r) => (r.temp_id === row.temp_id ? { ...r, ...row, screenshot_index: r.screenshot_index } : r)));
  const copyName = (row) => updateRow({ ...row, ...splitName(row.display_name) });
  const copySelected = () => setRows((list) => list.map((r) => (r.include && r.display_name.trim() ? { ...r, ...splitName(r.display_name) } : r)));

  const commit = async () => {
    if (selected.some((r) => !r.identifier.trim())) {
      setShowErrors(true);
      return setError(`Every selected row needs a ${label.toLowerCase()}. Fill it in or untick the row.`);
    }
    setSaving(true);
    setError("");
    try {
      const guests = selected.map(({ identifier, display_name, first_name, last_name }) => ({ identifier, display_name, first_name, last_name }));
      const { data } = await api.post(`/events/${id}/door-guests/import/commit`, { import_id: importId.current, guests });
      shots.forEach((s) => URL.revokeObjectURL(s.url));
      setShots([]);
      setResult(data);
    } catch (err) {
      setError(err.response ? formatApiError(err) : "Network error — nothing was lost. Tap Import again to retry.");
    } finally {
      setSaving(false);
    }
  };

  if (!existing && !error) return <PageLoader />;

  return (
    <div data-testid="door-import-page" className="font-manrope min-h-[100dvh] bg-slate-50 pb-32 text-slate-900">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <button type="button" data-testid="import-cancel" onClick={() => navigate(`/events/${id}`)} className="inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-slate-500"><ArrowLeft className="h-4 w-4" /> {result ? "Event" : "Cancel"}</button>
          <h1 className="font-display text-lg font-extrabold tracking-tight">Import from screenshots</h1>
          <span className="w-16" />
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-6 px-4 pt-5">
        {result ? <ResultView id={id} result={result} /> : (
          <>
            <ScreenshotStrip shots={shots} disabled={saving} onPick={pick} onView={setViewing} onRetry={(i) => extract(shots[i])} onRemove={removeShot} />
            {busy && <p data-testid="import-progress" className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Reading screenshots… {shots.filter((s) => s.status === "done" || s.status === "error").length}/{shots.length}</p>}
            {ordered.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-700">Review before importing. Nothing is saved yet.</p>
                <button type="button" data-testid="import-copy-selected" onClick={copySelected} className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700"><Copy className="h-3.5 w-3.5" /> Copy display names to real names (selected)</button>
              </div>
            )}
            <ul data-testid="import-rows" className="space-y-3">
              {ordered.map((r) => (
                <ImportCandidateCard key={r.temp_id} row={r} hint={hints[r.temp_id]} label={label} showError={showErrors}
                  onChange={updateRow} onCopyName={() => copyName(r)} onViewShot={() => setViewing(r.screenshot_index)}
                  onRemove={() => setRows((list) => list.filter((x) => x.temp_id !== r.temp_id))} />
              ))}
            </ul>
            <button type="button" data-testid="import-add-row" onClick={() => setRows((list) => [...list, manualRow()])} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700"><Plus className="h-4 w-4" /> Add row</button>
          </>
        )}
        {error && <p data-testid="import-error" role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</p>}
      </main>
      {!result && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
            <p data-testid="import-selected-count" className="text-sm font-semibold text-slate-600">{selected.length} selected</p>
            <button type="button" data-testid="import-commit" disabled={selected.length === 0 || saving || busy} onClick={commit} className="inline-flex min-h-[48px] items-center gap-2 rounded-xl bg-blue-700 px-5 text-base font-bold text-white disabled:opacity-50">
              {saving && <Loader2 className="h-5 w-5 animate-spin" />} Import {selected.length} guest{selected.length === 1 ? "" : "s"}
            </button>
          </div>
        </div>
      )}
      <ScreenshotViewer shot={viewing !== null ? shots[viewing] : null} index={viewing ?? 0} onClose={() => setViewing(null)} />
    </div>
  );
}
